import { useState } from "react";

const faqs = [
  {
    q: "What's the main difference between a solar generator and a gas generator?",
    a: "A solar generator uses a built-in lithium battery that can be recharged via solar panels, wall outlets, or car adapters — with zero fuel, zero emissions, and zero noise. A gas generator burns gasoline to produce power on demand, which means it runs loud, requires outdoor-only use due to carbon monoxide risk, and depends on fuel availability. Solar generators excel for indoor backup, medical devices, and long-term ownership value; gas generators shine for high-wattage needs and lowest upfront cost.",
  },
  {
    q: "Can a solar generator power my whole house?",
    a: "High-capacity solar generators (3,600W–5,000W+ with expanded battery banks) can power most essential circuits: refrigerator, lighting, phone chargers, CPAP machines, fans, a TV, and even a small window AC. They typically can't run central HVAC, electric stoves, or electric water heaters at full load simultaneously. If whole-home coverage for all appliances is your goal, a whole-home standby generator is the right fit.",
  },
  {
    q: "How long will a solar generator last during a power outage?",
    a: "Runtime depends on the battery capacity (watt-hours) and what you're powering. A 2,000Wh unit running a 60W refrigerator, a few LED lights, and phone charging could last 20–30 hours on a full charge. If you have solar panels connected, you'll continuously recharge during daylight hours — theoretically indefinitely. Most quality solar generators can supply 3–5 days of essential use before needing significant solar recharging.",
  },
  {
    q: "What is a whole-home standby generator and how does it work?",
    a: "A whole-home standby generator is permanently installed outside your home, connected to your natural gas or propane line and wired into your main electrical panel through an automatic transfer switch. When grid power goes out, it detects the loss within seconds and starts automatically — your lights barely flicker. It runs until grid power is restored, then shuts off automatically. No manual starting, no extension cords, no fuel runs.",
  },
  {
    q: "Is a gas generator safe to use indoors or in a garage?",
    a: "No — never. Gas generators produce carbon monoxide (CO), a colorless, odorless gas that can be fatal within minutes in enclosed spaces. They must be operated at least 20 feet from any door, window, or vent. Solar generators and battery stations produce zero emissions and are completely safe for indoor use.",
  },
  {
    q: "How much does it cost to install a whole-home standby generator?",
    a: "Installation typically runs $2,000–$5,000 on top of the unit cost, depending on your electrical panel, distance from the gas line, permit requirements, and local labor rates. Combined with unit costs of $3,000–$15,000, total installed costs usually fall in the $8,000–$20,000 range for most residential installs. Some high-end whole-home systems can exceed $25,000 fully installed.",
  },
  {
    q: "Which backup power system is best for someone on a budget?",
    a: "Gas generators offer the lowest entry price — you can get a capable 3,500W unit for $400–$700. However, when you factor in fuel, maintenance, and the ongoing cost of keeping it ready, the 5-year total can rival a mid-range solar generator. For the best combination of upfront value and long-term cost savings, a 1,000–2,000W solar generator in the $500–$1,200 range is hard to beat.",
  },
  {
    q: "Do solar generators work on cloudy days?",
    a: "Yes — solar panels still generate power on overcast days, typically at 10–30% of their rated output. You'll charge slower, but you'll still charge. Most quality solar generators also support wall outlet or car adapter charging, so you can top off before a storm hits and use solar to extend runtime during extended outages.",
  },
  {
    q: "What brands does Iron Haven Supply carry for backup power?",
    a: "Iron Haven Supply is an authorized reseller of EcoFlow, Nature's Generator, and Renogy — three of the most trusted names in solar generators and home backup power. Every product comes with full manufacturer warranty coverage. We don't carry generic or unverified brands.",
  },
  {
    q: "How do I choose the right size backup power system for my home?",
    a: "Start by listing the devices you need to run during an outage and their wattage. A refrigerator is ~150W, an LED TV ~50W, a CPAP machine ~30–60W, phone chargers ~20W each. Add them up for your baseline wattage, then choose a generator with at least 25% headroom above that total. For solar generators, multiply your wattage needs by the hours you want to run and that gives you the battery capacity (watt-hours) you need. Our team can help — call (866) 605-4975.",
  },
];

export default function ComparisonFAQ() {
  const [open, setOpen] = useState<number | null>(null);

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <section className="py-20 bg-gray-50 scroll-mt-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">FAQ</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2 mb-3">
            Questions We Hear All the Time
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Straight answers. No filler.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`rounded-xl border transition-all overflow-hidden ${
                open === i ? "border-amber-300 bg-white" : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-start gap-4 px-5 py-4 text-left cursor-pointer"
              >
                <div className={`w-6 h-6 flex items-center justify-center rounded-full shrink-0 mt-0.5 transition-colors ${open === i ? "bg-amber-500" : "bg-gray-100"}`}>
                  <i className={`text-xs transition-transform ${open === i ? "ri-subtract-line text-white" : "ri-add-line text-gray-500"}`}></i>
                </div>
                <span className={`font-semibold text-sm leading-snug flex-1 ${open === i ? "text-amber-700" : "text-gray-800"}`}>
                  {faq.q}
                </span>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pl-15">
                  <div className="pl-10">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm mb-4">Still have questions? We pick up the phone.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="tel:8666054975"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-6 py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm"></i>
              </div>
              Call (866) 605-4975
            </a>
            <a
              href="https://ironhavensupply.com/pages/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 border border-gray-200 hover:border-amber-400 text-gray-700 hover:text-amber-600 font-bold px-6 py-3 rounded-xl text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-sm"></i>
              </div>
              Send a Message
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
